import { useEffect, useRef, useState } from 'react';
import { preloader } from '../utils/ResourcePreloader';

// Custom Hook für Lazy Loading mit Intersection Observer
export function useLazyLoad(src, options = {}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  const {
    threshold = 0.01,
    rootMargin = '50px',
    placeholder = null,
    priority = 3, // Medium priority by default
    onLoad = () => {},
    onError = () => {}
  } = options;

  useEffect(() => {
    const element = imgRef.current;
    if (!element || !src) return;

    // Check if already loaded
    if (preloader.isLoaded(src)) {
      setImageSrc(src);
      setIsLoaded(true);
      onLoad();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);

          // Load image with preloader
          preloader.addResource(src, priority, 'image')
            .then(() => {
              setImageSrc(src);
              setIsLoaded(true);
              onLoad();
            })
            .catch((error) => {
              console.error('Failed to load image:', error);
              onError(error);
            });

          // Unobserve after triggering load
          observer.unobserve(element);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [src, threshold, rootMargin, priority, onLoad, onError]);

  return {
    imgRef,
    imageSrc: imageSrc || placeholder,
    isLoaded,
    isInView
  };
}

// Hook für Progressive Image Loading
export function useProgressiveImage(lowQualitySrc, highQualitySrc, options = {}) {
  const [src, setSrc] = useState(lowQualitySrc);
  const [isLoading, setIsLoading] = useState(true);
  const [blur, setBlur] = useState(true);

  useEffect(() => {
    if (!highQualitySrc) return;

    // Lade hochauflösendes Bild
    const img = new Image();
    img.src = highQualitySrc;

    img.onload = () => {
      setSrc(highQualitySrc);
      setIsLoading(false);

      // Entferne Blur-Effekt nach kurzer Zeit
      setTimeout(() => setBlur(false), 100);
    };

    img.onerror = () => {
      setIsLoading(false);
    };
  }, [lowQualitySrc, highQualitySrc]);

  return {
    src,
    isLoading,
    blur,
    style: {
      filter: blur ? 'blur(5px)' : 'none',
      transition: 'filter 0.3s ease-in-out'
    }
  };
}

// Hook für Viewport Detection
export function useInViewport(options = {}) {
  const [isInViewport, setIsInViewport] = useState(false);
  const [hasBeenInView, setHasBeenInView] = useState(false);
  const elementRef = useRef(null);

  const {
    threshold = 0.1,
    rootMargin = '0px',
    freezeOnceVisible = false
  } = options;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const inView = entry.isIntersecting;
        setIsInViewport(inView);

        if (inView && !hasBeenInView) {
          setHasBeenInView(true);
        }

        if (freezeOnceVisible && inView) {
          observer.unobserve(element);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, freezeOnceVisible, hasBeenInView]);

  return {
    elementRef,
    isInViewport,
    hasBeenInView
  };
}

// Hook für Preload Management
export function usePreloadManager(currentRoute) {
  useEffect(() => {
    if (!currentRoute) return;

    // Importiere Route Assets
    import('../utils/ResourcePreloader').then(({ ROUTE_ASSETS, preloader }) => {
      const currentAssets = ROUTE_ASSETS[currentRoute];

      if (currentAssets) {
        // Lade Assets für aktuelle Route
        preloader.preloadRoute(currentAssets);
      }

      // Preload andere Routen mit niedriger Priorität
      Object.keys(ROUTE_ASSETS).forEach(route => {
        if (route !== currentRoute) {
          const assets = ROUTE_ASSETS[route];
          // Ändere Priorität auf IDLE für andere Routen
          const idleAssets = assets.map(asset => ({
            ...asset,
            priority: 5 // IDLE priority
          }));

          // Verzögere das Preloading anderer Routen
          setTimeout(() => {
            preloader.preloadRoute(idleAssets);
          }, 2000);
        }
      });
    });
  }, [currentRoute]);
}

// Hook für Resource Hints
export function useResourceHints(resources = []) {
  useEffect(() => {
    resources.forEach(resource => {
      const link = document.createElement('link');

      switch (resource.type) {
        case 'dns-prefetch':
          link.rel = 'dns-prefetch';
          link.href = resource.url;
          break;
        case 'preconnect':
          link.rel = 'preconnect';
          link.href = resource.url;
          if (resource.crossorigin) {
            link.crossOrigin = 'anonymous';
          }
          break;
        case 'prefetch':
          link.rel = 'prefetch';
          link.as = resource.as || 'document';
          link.href = resource.url;
          break;
        case 'preload':
          link.rel = 'preload';
          link.as = resource.as || 'image';
          link.href = resource.url;
          if (resource.type === 'font') {
            link.crossOrigin = 'anonymous';
          }
          break;
        default:
          break;
      }

      document.head.appendChild(link);

      // Cleanup
      return () => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      };
    });
  }, [resources]);
}